import { supabase } from './supabaseClient'
import type { Formatters, Profile, Rule, Subscription, TranslateResult } from '../types'

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8000'

let _cachedToken: string | null = null
let _tokenExpiresAt = 0

supabase.auth.onAuthStateChange((_event, session) => {
  if (session?.access_token) {
    _cachedToken = session.access_token
    _tokenExpiresAt = session.expires_at ?? 0
  } else {
    _cachedToken = null
    _tokenExpiresAt = 0
  }
})

async function authHeaders(required: boolean): Promise<Record<string, string>> {
  const now = Math.floor(Date.now() / 1000)
  if (_cachedToken && _tokenExpiresAt > now + 30) {
    return { Authorization: `Bearer ${_cachedToken}` }
  }
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('getSession timeout')), 5000),
      ),
    ])
    const token = result.data.session?.access_token
    if (token) {
      _cachedToken = token
      _tokenExpiresAt = result.data.session?.expires_at ?? 0
      return { Authorization: `Bearer ${token}` }
    }
  } catch {
    // fall through
  }
  if (required) throw new Error('not authenticated')
  return {}
}

// ──────────────────────────────────────────────────────────────
// snake_case → camelCase recursive converter. Matches the pattern used
// in frontend/src/lib/api.ts and commons/src/lib/api.ts so response
// shapes are uniform across every Chrona surface. Request bodies are
// NOT converted — the backend expects snake_case on input.
// ──────────────────────────────────────────────────────────────

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

function convertKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(convertKeys)
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
        snakeToCamel(k),
        convertKeys(v),
      ]),
    )
  }
  return obj
}

// Batch 4.5: optional correlationId travels across api functions as
// the X-Chrona-Correlation-ID header so signup-chain calls can be
// grepped end-to-end in Railway logs (see plan §7.2). No-op for
// callers that don't pass the option.
async function request<T>(
  path: string,
  init: RequestInit & { authRequired?: boolean; correlationId?: string } = {},
): Promise<T> {
  const { authRequired = true, correlationId, headers, ...rest } = init
  const auth = await authHeaders(authRequired)
  const corr: Record<string, string> = correlationId
    ? { 'X-Chrona-Correlation-ID': correlationId }
    : {}
  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...auth,
      ...corr,
      ...(headers || {}),
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const err = new Error(`${res.status} ${res.statusText}: ${text}`) as Error & {
      status?: number
      body?: string
    }
    err.status = res.status
    err.body = text
    throw err
  }
  if (res.status === 204) return undefined as T
  return convertKeys(await res.json()) as T
}

// ──────────────────────────────────────────────────────────────
// Profile
// ──────────────────────────────────────────────────────────────

export interface ProfileResponse {
  profileId: string | null
  domain: string
  manifestVersion: number
  manifestToken: string | null
  subscription: Subscription
  rules: Rule[]
  // A2F-537: pack IDs the user has selected. Used to re-hydrate the
  // PackRail toggle state in the configurator. Executable rules always
  // live in `rules` — `packs` is bookkeeping, not executed by the
  // add-in.
  packs?: string[]
  // A2F-554: tag keys the user explicitly selected. Ground truth for
  // the OnboardPage returning-hero SummaryStats — inferring from rules
  // alone loses tags with no rule mappings, and the backend already
  // stores this verbatim on every signup via buildProfile().
  tags?: string[]
  // A2F-670: house-style overrides keyed by formatter group. Present
  // when the user explicitly customized formatting on the FRE redesign;
  // absent when the user inherited Flavia's CHRONA_DEFAULT (D70).
  // ConfiguratorPage hydrates field cards from this on /configure load.
  formatters?: Formatters
  // A2F-796: packs the user toggled off from the My Chrona pack-grouped
  // surface. The configurator UI doesn't edit this field; it must
  // round-trip through ConfiguratorPage hydration → save body so a
  // wholesale JSONB replace on the backend doesn't silently re-enable
  // packs the user disabled.
  disabledPacks?: string[]
}

export function getProfile(
  opts: { correlationId?: string } = {},
): Promise<ProfileResponse> {
  return request<ProfileResponse>('/api/v1/ribbon/profile', {
    correlationId: opts.correlationId,
  })
}

export function saveProfile(
  domain: string,
  profile: Profile,
  opts: { correlationId?: string } = {},
): Promise<ProfileResponse> {
  return request<ProfileResponse>('/api/v1/ribbon/profile', {
    method: 'PUT',
    body: JSON.stringify({ domain, profile }),
    correlationId: opts.correlationId,
  })
}

// ──────────────────────────────────────────────────────────────
// Translate
// ──────────────────────────────────────────────────────────────

// A2F-536: `sourceDetail` distinguishes regex-era cache hits from
// tool-use-era cache hits during the LLM tool-use rollout. Always
// present post-A2F-536; pre-migration cached rows fall back to
// 'cache_regex' server-side. Transient upstream failures use
// `llm_tool_use_error` so rollout dashboards don't conflate them
// with successes.
export type TranslateSourceDetail =
  | 'catalog'
  | 'cache_regex'
  | 'cache_tool_use'
  | 'llm_tool_use'
  | 'llm_tool_use_error'
  | 'llm_unsupported_provider'

export interface TranslateResponse {
  source: 'catalog' | 'cache' | 'llm'
  sourceDetail?: TranslateSourceDetail
  result: TranslateResult
}

export function translateRule(
  requestText: string,
  domain: string,
): Promise<TranslateResponse> {
  return request<TranslateResponse>('/api/v1/ribbon/translate-rule', {
    method: 'POST',
    authRequired: false,
    body: JSON.stringify({ request_text: requestText, domain }),
  })
}

// ──────────────────────────────────────────────────────────────
// Subscription
// ──────────────────────────────────────────────────────────────

export function getSubscription(): Promise<Subscription> {
  return request<Subscription>('/api/v1/ribbon/subscription')
}

export async function startTrial(): Promise<Subscription> {
  try {
    return await request<Subscription>('/api/v1/ribbon/subscription/start-trial', {
      method: 'POST',
    })
  } catch (e) {
    const err = e as { status?: number }
    if (err.status === 409) {
      // Already started — treat as success, fetch current.
      return getSubscription()
    }
    throw e
  }
}

// ──────────────────────────────────────────────────────────────
// Signup — hits backend /auth/signup, NOT Supabase client.
// The backend is email-enumeration resistant; calling supabase.auth.signUp
// directly would leak registration state.
// ──────────────────────────────────────────────────────────────

export interface SignupBody {
  email: string
  password: string
  display_name?: string
  organization_name?: string
  role_title?: string
  terms_version?: string
  source?: 'ctdcommons' | 'chrona_pro'
}

export function signup(
  body: SignupBody,
  opts: { correlationId?: string } = {},
): Promise<{ message: string }> {
  return request<{ message: string }>('/api/v1/auth/signup', {
    method: 'POST',
    authRequired: false,
    body: JSON.stringify(body),
    correlationId: opts.correlationId,
  })
}

// ──────────────────────────────────────────────────────────────
// Pending profile — cross-device signup recovery (A2F-538). See
// backend/services/pending_profile_service.py for full rationale.
// The PUT is unauthenticated (the user is about to sign up with
// this email); the PROMOTE call takes no body because the lookup
// key is the server-side authenticated session's email.
// ──────────────────────────────────────────────────────────────

export interface PendingProfileBody {
  email: string
  profile: Profile
  tier_choice?: string | null
  terms_version?: string | null
  signup_metadata?: {
    display_name?: string
    organization_name?: string
    role_title?: string
  }
}

export function putPendingProfile(
  body: PendingProfileBody,
  opts: { correlationId?: string } = {},
): Promise<{ status: string }> {
  return request<{ status: string }>('/api/v1/ribbon/pending-profile', {
    method: 'POST',
    authRequired: false,
    body: JSON.stringify(body),
    correlationId: opts.correlationId,
  })
}

export interface PromoteResponse {
  promoted: boolean
  profile: ProfileResponse
}

export async function promotePendingProfile(
  opts: { correlationId?: string } = {},
): Promise<PromoteResponse | null> {
  try {
    return await request<PromoteResponse>(
      '/api/v1/ribbon/promote-pending-profile',
      { method: 'POST', correlationId: opts.correlationId },
    )
  } catch (e) {
    // 404 = no pending row (or expired, or stale-pending guard fired).
    // Surface as `null` so callers can render the empty-profile UX
    // without an error branch.
    const err = e as { status?: number }
    if (err.status === 404) return null
    throw e
  }
}

// ──────────────────────────────────────────────────────────────
// Session — resolves the Chrona user UUID from the current Supabase
// access token. Used by telemetry.identifyUser() so PostHog events
// merge with the same user's activity on the Pro taskpane and the
// main app, per A2F-434.
// ──────────────────────────────────────────────────────────────

export interface SessionResponse {
  authorized: boolean
  chronaUserId: string
  userEmail: string
  displayName: string
  mode: string
  activeOrgId: string | null
  telemetryEnabled: boolean
}

export function fetchSession(): Promise<SessionResponse> {
  return request<SessionResponse>('/api/v1/ribbon/session', { method: 'POST' })
}

// ──────────────────────────────────────────────────────────────
// Manifest URL (constructed client-side; no fetch)
// ──────────────────────────────────────────────────────────────

export function manifestUrl(token: string, version: number): string {
  return `${API_BASE}/api/v1/ribbon/manifest/${token}?v=${version}`
}

// ──────────────────────────────────────────────────────────────
// Billing (A2F-767) — Stripe Checkout + Customer Portal
//
// Server-side Checkout sessions only — no Stripe.js on the client.
// AccountPage redirects via window.location.href. Errors with the
// shape { detail: { error: 'stripe_not_configured' } } indicate the
// backend hasn't been wired up to Stripe yet (local dev) and are
// rendered as a "billing not available" notice.
// ──────────────────────────────────────────────────────────────

export interface BillingStatus {
  plan: 'free' | 'pro'
  status: 'active' | 'past_due' | 'canceled' | 'incomplete' | string | null
  currentPeriodEnd: string | null
  hasCustomer: boolean
}

export function getBillingStatus(): Promise<BillingStatus> {
  return request<BillingStatus>('/api/v1/ribbon/billing/status')
}

export function createCheckoutSession(): Promise<{ checkoutUrl: string }> {
  return request<{ checkoutUrl: string }>(
    '/api/v1/ribbon/billing/create-checkout-session',
    { method: 'POST' },
  )
}

export function createPortalSession(): Promise<{ portalUrl: string }> {
  return request<{ portalUrl: string }>(
    '/api/v1/ribbon/billing/create-portal-session',
    { method: 'POST' },
  )
}
